import { useState } from 'react';

import { useTranslation } from '@klv/hooks/useTranslation';
import { useModal } from '@klv/context/modal';
import { toUrlType } from './utils/functions';
import { withOptimizely } from '@optimizely/react-sdk';
import ReactPlayer from 'react-player/vimeo';
import Modal from '@klv/organisms/modal';
import ButtonComponent from './variants/button';
import HubspotForm from '@klv/atoms/hubspot-form';
import LinkComponent from './variants/link';
import { generateClassName } from '@klv/shared/utils/functions';
import type { ReactSDKClient, WithOptimizelyProps } from '@optimizely/react-sdk';
import type { IButtonProps, TQueryActionProps } from './types';
import * as styles from './styles/button.module.scss';

const DEMO = '/demo-request';
const SIGNUP = '/sign-up';

const clickHandler = ({ action, optimizely, text }: { action?: () => void; optimizely: ReactSDKClient | null; text?: string; }) => {
  if (
    optimizely
    && (text === 'Sign up' || text === 'Get a demo')
  ) {
    return () => {
      let metric = 'global_cta_click_signup';

      if (text === 'Get a demo') {
        metric = 'global_cta_click_demo';
      }

      optimizely.track(metric);

      if (action) {
        action();
      }
    };
  }

  return action;
};

const Button = ({
  action,
  buttonColor = 'dark',
  children,
  openInNewTab,
  buttonAction,
  kFormId = '',
  hubspotFormOptions = { hubspotFormId: '' },
  setTabIndex = 0,
  text,
  to = '',
  url = '',
  variant = 'primary',
  optimizely,
  optimizelyReadyTimeout,
  isServerSide,
  isDisabled = false,
  ...props
}: IButtonProps & WithOptimizelyProps) => {
  const modalStore = useModal();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showHubspotModal, setShowHubspotModal] = useState(false);
  const { t } = useTranslation();
  const pathPrefix = t('langCodes_pathPrefix');
  const queryActions: TQueryActionProps = {
    '?modal=demo': {
      link: pathPrefix ? `/${pathPrefix}${DEMO}` : DEMO,
      parameters: ['demo'],
    },
    '?modal=signup': {
      link: pathPrefix ? `/${pathPrefix}${SIGNUP}` : SIGNUP,
      parameters: ['signup'],
    },
  };

  const hasLegacyModalTrigger = url?.match(/^\?/) && queryActions[url];
  const hasModalTrigger = url?.match(/^\?modal=/) && !queryActions[url];

  const sharedProps = {
    variant,
    buttonColor,
    setTabIndex,
    text,
    isDisabled: isDisabled || undefined,
  };

  if (hasLegacyModalTrigger) {
    return (
    <LinkComponent
      action={clickHandler({ action, optimizely, text })}
      openInNewTab={openInNewTab}
      url={queryActions[url].link}
      {...props}
      {...sharedProps}
    >
      {children}
    </LinkComponent>
    );
  }
  if (buttonAction === 'video_modal' && url?.includes('vimeo')) {
    return (
      <>
        {showVideoModal && (
            <Modal
              contentPadding="30"
              close={() => setShowVideoModal(false)}
              show={showVideoModal}
              size="constrained"
              backgroundColor="lightSand"
              isMediaModal
            >
              <ReactPlayer
                url={url}
                className={generateClassName(['react-player', styles.modalVideo])}
                controls
                playing
                loop={false}
                width="100%"
                height="auto"
                config={{ playerOptions:{ texttrack: t('langCodes_recaptchaLanguage') === 'en' ? '' : t('langCodes_recaptchaLanguage') } } }  
              />
            </Modal>
        )}
      <ButtonComponent
        action={clickHandler({ action: () => setShowVideoModal(true), optimizely, text })}
        openInNewTab={openInNewTab}
        {...props}
        {...sharedProps}
      >
        {children}
    </ButtonComponent>
      </>
    );
  } else if (hasModalTrigger) {
    return (
      <ButtonComponent
        action={ () => {
          modalStore.open(url);
        } }
        openInNewTab={openInNewTab}
        {...props}
        {...sharedProps}
      >
        {children}
      </ButtonComponent>
    );
  } else if (buttonAction === 'k_form') {
    // like this but open modal
    return (
      <ButtonComponent
      action={clickHandler({ action: () => {
        if (window?.klaviyo) {
          window.klaviyo.openForm(kFormId);
        }
      }, optimizely, text })}
        openInNewTab={openInNewTab}
        {...props}
        {...sharedProps}
      >
        {children}
    </ButtonComponent>
    );
  } else if (buttonAction === 'hubspot_form') {
    return (
      <>
        {showHubspotModal && (
            <Modal
              close={() => setShowHubspotModal(false)}
              show={showHubspotModal}
              size="constrained"
              backgroundColor="lightSand"
              isMediaModal
              className={!hubspotFormOptions.hubspotFormImage ? styles.hubspotModal : ''}
            >
              <HubspotForm
                formId={hubspotFormOptions.hubspotFormId}
                hubspotHeaderText={hubspotFormOptions.hubspotHeaderText}
                hubspotSubheaderText={hubspotFormOptions.hubspotSubheaderText}
                image={hubspotFormOptions.hubspotFormImage}
                redirectUrl={hubspotFormOptions.hubspotRedirectUrl}
              />
            </Modal>
        )}
      <ButtonComponent
        action={clickHandler({ action: () => setShowHubspotModal(true), optimizely, text })}
        openInNewTab={openInNewTab}
        {...props}
        {...sharedProps}
      >
        {children}
    </ButtonComponent>
      </>
    );
  }


  if ((Boolean(!url) && Boolean(!to))) {
    return (
      <ButtonComponent
        action={clickHandler({ action, optimizely, text })}
        {...props}
        {...sharedProps}
      >
        {children}
      </ButtonComponent>
    );
  }

  return (
    <LinkComponent
      action={clickHandler({ action, optimizely, text })}
      openInNewTab={openInNewTab}
      url={toUrlType(url)}
      to={toUrlType(to)}
      {...props}
      {...sharedProps}
    >
      {children}
    </LinkComponent>
  );
};

/* @ts-ignore: optimizely */
export default withOptimizely(Button);
